import { z } from "zod";

const WoWChangeSchema = z.object({
  current_value: z.number().nullable().optional(),
  previous_value: z.number().nullable().optional(),
  change_pct: z.number().nullable(),
  change_absolute: z.number().nullable().optional(),
  direction: z.enum(["up", "down", "stable", "increasing", "decreasing"]),
});

const BudgetSchema = z.object({
  total_budget: z.number().nullable(),
  mtd_spend: z.number().nullable(),
  pacing_pct: z.number().nullable(),
  overpacing_campaigns: z.number().nullable(),
  month_elapsed_pct: z.number().nullable().optional(),
  pacing_status: z.string().nullable().optional(),
});

const MetricsSchema = z.object({
  total_spend: z.number().nullable().optional(),
  total_impressions: z.number().nullable().optional(),
  total_clicks: z.number().nullable().optional(),
  total_conversions: z.number().nullable().optional(),
  ctr: z.number().nullable().optional(),
  cac: z.number().nullable(),
  roas: z.number().nullable(),
  cpc: z.number().nullable().optional(),
  cpm: z.number().nullable().optional(),
  active_campaigns: z.number().nullable(),
  active_adsets: z.number().nullable().optional(),
});

export const AdsIntelligenceOverviewSchema = z.object({
  account_id: z.string().optional(),
  currency: z.string(),
  metrics: MetricsSchema,
  wow_changes: z.object({
    cac: WoWChangeSchema,
    roas: WoWChangeSchema,
    spend: WoWChangeSchema,
    ctr: WoWChangeSchema.optional(),
  }),
  budget: BudgetSchema,
});

const InsightSchema = z.object({
  title: z.string(),
  description: z.string(),
  severity: z.string(),
  category: z.string(),
  source: z.string(),
});

export const AdsIntelligenceInsightsSchema = z.object({
  insights: z.array(InsightSchema),
});

const TrendSeriesItemSchema = z.object({
  period_start: z.string(),
  spend: z.number().nullable(),
  cac: z.number().nullable(),
});

export const AdsIntelligenceTrendsSchema = z.object({
  series: z.array(TrendSeriesItemSchema),
});

// ── Schedules API ──
const ScheduleFrequencySchema = z.enum(["daily", "weekly", "monthly"]);

const ScheduleStatusSchema = z.enum(["active", "paused"]);

const ScheduleComparisonSchema = z.enum(["previous_period", "same_period_last_year"]);

const ScheduleItemSchema = z.object({
  id: z.string(),
  organization_id: z.string(),
  user_id: z.string(),
  name: z.string(),
  prompt: z.string(),
  frequency: ScheduleFrequencySchema,
  time_of_day: z.string(),
  day_config: z.record(z.unknown()).nullable(),
  date_range: z.string(),
  comparison: ScheduleComparisonSchema,
  notify_email: z.boolean(),
  email_recipients: z.array(z.string()),
  notify_slack: z.boolean(),
  notify_in_app: z.boolean(),
  ad_account_id: z.string().nullable(),
  source: z.string(),
  status: ScheduleStatusSchema,
  paused_reason: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  total_runs: z.number().int().nonnegative(),
  next_run_at: z.string().nullable(),
});

export const ScheduleResponseSchema = ScheduleItemSchema;
export const ScheduleListResponseSchema = z.array(ScheduleItemSchema);

// ── Reports API ──
const DateRangeSchema = z.object({
  since: z.string(),
  until: z.string(),
});

const ReportMetadataSchema = z.object({
  title: z.string(),
  thread_id: z.string().nullable(),
  sandbox_id: z.string().nullable(),
  description: z.string().nullable(),
  dependencies: z.array(z.string()),
  dashboard_code: z.string().nullable(),
  install_command: z.string().nullable(),
});

export const ReportItemSchema = z.object({
  id: z.string(),
  schedule_id: z.string(),
  schedule_name: z.string(),
  organization_id: z.string(),
  triggered_at: z.string(),
  started_at: z.string().nullable().optional(),
  completed_at: z.string().nullable().optional(),
  status: z.string(),
  failure_reason: z.string().nullable().optional(),
  prompt_used: z.string().nullable().optional(),
  date_range_resolved: DateRangeSchema.nullable().optional(),
  comparison_range_resolved: DateRangeSchema.nullable().optional(),
  report_summary: z.string().nullable().optional(),
  report_content: z.string().nullable().optional(),
  report_metadata: ReportMetadataSchema.nullable().optional(),
  is_read: z.boolean(),
  read_at: z.string().nullable().optional(),
  created_at: z.string(),
});

export const ReportListResponseSchema = z.object({
  items: z.array(ReportItemSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
});

export type AdsIntelligenceTrends = z.infer<typeof AdsIntelligenceTrendsSchema>;
export type TrendSeriesItem = z.infer<typeof TrendSeriesItemSchema>;
export type AdsInsight = z.infer<typeof InsightSchema>;
export type AdsIntelligenceInsights = z.infer<typeof AdsIntelligenceInsightsSchema>;
export type AdsIntelligenceOverview = z.infer<typeof AdsIntelligenceOverviewSchema>;
export type WoWChange = z.infer<typeof WoWChangeSchema>;
export type ScheduleItem = z.infer<typeof ScheduleItemSchema>;
export type ScheduleStatus = z.infer<typeof ScheduleStatusSchema>;
export type ScheduleFrequency = z.infer<typeof ScheduleFrequencySchema>;
export type ScheduleComparison = z.infer<typeof ScheduleComparisonSchema>;
export type ReportItem = z.infer<typeof ReportItemSchema>;
export type ReportListResponse = z.infer<typeof ReportListResponseSchema>;
export type ReportMetadata = z.infer<typeof ReportMetadataSchema>;
export type DateRange = z.infer<typeof DateRangeSchema>;
